import { XMarkIcon } from '@heroicons/react/24/outline';
import { type NodeProps } from '@xyflow/react';
import { FileCode } from 'lucide-react';
import { memo } from 'react';

import { AttackGraphIcon } from '@/sections/attacks/attackPaths/components/icons/AttackGraphIcon';
import { PraetorianAttackerIcon } from '@/sections/attacks/attackPaths/components/icons/PraetorianAttackerIcon';
import { NodeHandles } from '@/sections/attacks/attackPaths/components/nodes/NodeHandles';
import { useAttackState } from '@/sections/attacks/attackPaths/context';
import type { CustomAttackerNode } from '@/sections/attacks/attackPaths/types';
import { cn } from '@/utils/classname';

/**
 * Attacker node component for attack path visualization.
 *
 * Supports three variants:
 * - Default / Entry Point: Red skull icon with red-500 border
 * - Infrastructure: Custom icon with brand crimson border and skull badge
 * - Payload: FileCode icon with brand crimson border and skull badge
 *
 * Features:
 * - Edit mode: Remove button and connection handles visible
 * - Infrastructure/Payload variants show custom icon, label, and optional metadata
 */
function AttackerNodeComponent({
  data,
  id,
  sourcePosition,
  targetPosition,
}: NodeProps<CustomAttackerNode>) {
  const { isEditMode, removeNode } = useAttackState();
  const isInfra = data.variant === 'infrastructure';
  const isPayload = data.variant === 'payload';
  const isBrandVariant = isInfra || isPayload;
  const label = data.label || 'Attacker';

  return (
    <>
      {/* (130-72)/2=29px top spacer centers the 72px circle's handle at dims.height/2=65 (see getNodeDimensions in utils/computeLayout.ts) */}
      <div className="relative flex w-[120px] flex-col items-center pt-[29px]">
        {/* Circular node container - 72x72px */}
        <div
          className={cn(
            'relative flex size-[72px] items-center justify-center rounded-full border-2',
            isBrandVariant
              ? 'border-chariot-brand-primary bg-chariot-brand-primary/10'
              : 'border-red-500 bg-red-500/10'
          )}
        >
          {isPayload ? (
            <FileCode size={32} className="text-chariot-brand-primary" />
          ) : isInfra && data.iconOverride ? (
            <AttackGraphIcon
              icon={data.iconOverride}
              size={32}
              className="text-chariot-brand-primary"
            />
          ) : (
            <PraetorianAttackerIcon
              className={cn(
                'size-8',
                isInfra ? 'text-chariot-brand-primary' : 'text-red-500'
              )}
            />
          )}

          {/* Praetorian skull badge for infrastructure/payload variants */}
          {isBrandVariant && (
            <div className="border-chariot-background-primary bg-chariot-brand-primary absolute -right-0.5 -bottom-0.5 flex size-5 items-center justify-center rounded-full border-2">
              <PraetorianAttackerIcon className="size-2.5 text-white" />
            </div>
          )}

          {isEditMode && (
            <button
              className="bg-chariot-status-critical hover:bg-chariot-status-critical/80 absolute -top-1 -right-1 rounded-full p-[2px]"
              onClick={event => {
                event.stopPropagation();
                removeNode(id);
              }}
              aria-label={`Remove ${isInfra ? 'infrastructure' : isPayload ? 'payload' : 'attacker'} node`}
            >
              <XMarkIcon className="text-chariot-text-primary size-3 stroke-[4px]" />
            </button>
          )}

          <NodeHandles
            isEditMode={isEditMode}
            nodeId={id}
            sourcePosition={sourcePosition}
            targetPosition={targetPosition}
          />
        </div>
        {/* Node label below */}
        <div className="mt-2 w-max min-w-full text-center text-sm font-medium wrap-break-word">
          {label}
        </div>
        {/* Metadata below label for infrastructure/payload variants */}
        {isBrandVariant && data.metadata && (
          <div className="text-chariot-text-secondary mt-0.5 w-max min-w-full text-center text-xs wrap-break-word">
            {data.metadata}
          </div>
        )}
      </div>
    </>
  );
}

export const AttackerNode = memo(AttackerNodeComponent);
